import { useState, type MouseEvent } from 'react';
import {
  TableCell, TableSortLabel, IconButton, Popover, Box, Stack, Typography,
  TextField, Checkbox, FormControlLabel, Button, InputAdornment,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ColumnFilter, SortDir } from './useTableSortFilter';

export interface PmHeadCellProps {
  label: string;
  align?: 'left' | 'right' | 'center';
  /** Default: true. */
  sortable?: boolean;
  filter?: ColumnFilter;
  sortActive?: boolean;
  sortDir?: SortDir;
  onToggleSort?: () => void;
  filterValue?: string | string[];
  onSetFilter?: (value: string | string[]) => void;
  onClearFilter?: () => void;
  /** Basis-Styling der Kopfzelle (Farbe/Gewicht). */
  headSx?: SxProps<Theme>;
  /** Zusätzliches Styling für das Label selbst (z. B. Titel-Spalte optisch
   *  prominenter) — überschreibt den Default-Weight des Sort-Labels. */
  labelSx?: SxProps<Theme>;
  minWidth?: number;
}

function isActive(v: string | string[] | undefined): boolean {
  if (v == null) return false;
  return Array.isArray(v) ? v.length > 0 : v.trim().length > 0;
}

/**
 * Tabellenkopf-Zelle mit optionaler Klick-Sortierung (TableSortLabel) und
 * optionalem Filter-Popover (Freitext oder Mehrfach-Auswahl). Ersetzt die
 * frueher externe Filter-Toolbar — Sortierung und Filter leben jetzt im Kopf.
 */
export function PmHeadCell({
  label, align = 'left', sortable = true, filter,
  sortActive = false, sortDir = 'asc', onToggleSort,
  filterValue, onSetFilter, onClearFilter, headSx, labelSx, minWidth,
}: PmHeadCellProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const filterOn = isActive(filterValue);

  const openPopover = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const closePopover = () => setAnchor(null);

  const enumValues = Array.isArray(filterValue) ? filterValue : [];
  const toggleEnum = (value: string) => {
    const next = enumValues.includes(value)
      ? enumValues.filter((v) => v !== value)
      : [...enumValues, value];
    onSetFilter?.(next);
  };

  return (
    <TableCell sx={{ ...headSx, ...(minWidth ? { minWidth } : {}) }} align={align}>
      <Stack
        direction="row"
        spacing={0.25}
        alignItems="center"
        justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}
      >
        {sortable && onToggleSort ? (
          <TableSortLabel
            active={sortActive}
            direction={sortActive ? sortDir : 'asc'}
            onClick={onToggleSort}
            sx={{ fontWeight: 600, color: 'inherit !important', ...labelSx }}
          >
            {label}
          </TableSortLabel>
        ) : (
          <Box component="span" sx={labelSx}>{label}</Box>
        )}

        {filter && (
          <IconButton
            size="small"
            onClick={openPopover}
            aria-label={`${label} filtern`}
            sx={{ color: filterOn ? 'var(--rittec-petrol, #1D8296)' : 'var(--pm-fg-subtle)' }}
          >
            <FilterListIcon fontSize="inherit" />
          </IconButton>
        )}
      </Stack>

      {filter && (
        <Popover
          open={open}
          anchorEl={anchor}
          onClose={closePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 1.5, minWidth: 220, maxWidth: 280 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {label}
              </Typography>
              {filterOn && (
                <Button
                  size="small"
                  onClick={() => { onClearFilter?.(); }}
                  sx={{ minWidth: 0, fontSize: '0.7rem' }}
                >
                  Zurücksetzen
                </Button>
              )}
            </Stack>

            {filter.type === 'text' && (
              <TextField
                autoFocus
                size="small"
                fullWidth
                placeholder="Enthält…"
                value={typeof filterValue === 'string' ? filterValue : ''}
                onChange={(e) => onSetFilter?.(e.target.value)}
                InputProps={{
                  endAdornment: filterOn ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => onClearFilter?.()} aria-label="Filter leeren">
                        <ClearIcon fontSize="inherit" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            )}

            {filter.type === 'enum' && (
              <Stack sx={{ maxHeight: 260, overflowY: 'auto' }}>
                {filter.options.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={enumValues.includes(opt.value)}
                        onChange={() => toggleEnum(opt.value)}
                      />
                    }
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Popover>
      )}
    </TableCell>
  );
}
